// Item 409 — superusers mark what really happened at the train (the owner's mockup option B, three marks), see
// the clue's own answer in words, who else marked, and how the clue's answers have been judged so far.
// PoseReviewPanel gates (superuser + a stored pose) and reads the SAME cached pose as the overlay
// (queryKeys.eventPose), so the pose is fetched once. A mark shows as pressed only once the server saved it.
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { eventsApi } from '@/api/endpoints'
import { POSE_STALE_MS, queryKeys } from '@/config/queryConfig'
import { PoseGate } from './PoseGate'
import { useAuthStore } from '@/store/authStore'
import { cn, formatDateTime } from '@/lib/utils'
import type { Event, EventPose, ReviewVerdict } from '@/types'

const CLUE = 'hand_at_train'
const VERDICTS: ReviewVerdict[] = ['graffiti', 'hand_not_graffiti', 'no_hand']
const KNOWN_REASONS = [
  'no_fps', 'no_usable_wrist', 'no_vehicle', 'span_too_long', 'no_repeated_motion',
  'feet_moving', 'not_at_train', 'feet_moving_not_at_train', 'pose_busy', 'pose_failed',
] as const
type KnownReason = (typeof KNOWN_REASONS)[number]
const knownReason = (reason: string | null): KnownReason | null => {
  const key = reason?.startsWith('pose_failed') ? 'pose_failed' : reason
  return KNOWN_REASONS.find((k) => k === key) ?? null
}

const seconds = (n: number) => (Math.round(n * 10) / 10).toFixed(1)

/** The clue's answer in words, for the reviewer: "yes, 1.2–4.6 s", "no (reason)", "could not be checked (reason)",
 * then where it happened when known (at a train / vehicle, or not): recorded, never required. */
function ClueAnswer({ pose }: { pose: EventPose }) {
  const { t } = useTranslation('events')
  const { state, reason, start_s, end_s, at_vehicle, box_kind } = pose.hand_at_train
  const key = knownReason(reason)
  const why = state !== 'yes' && key ? ` (${t(`review.reason.${key}`)})` : ''
  const place = at_vehicle === true ? t(box_kind === 'train' ? 'review.place.train' : 'review.place.vehicle')
    : at_vehicle === false ? t('review.place.away') : ''
  let answer: string
  if (state === 'yes') {
    answer = start_s != null && end_s != null
      ? t('review.answer.yesStretch', { start: seconds(start_s), end: seconds(end_s) })
      : t('review.answer.yes')
  } else if (state === 'no') {
    answer = t('review.answer.no')
  } else {
    answer = t('review.answer.notAssessed')
  }
  return <p className="text-sm">{t('review.clue')}: <span className="font-semibold">{answer}</span>{why}{place}</p>
}

function PoseReviewMarks({ eventId, pose }: { eventId: number; pose: EventPose }) {
  const { t } = useTranslation('events')
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user?.username)

  const summary = useQuery({
    queryKey: queryKeys.reviewSummary(CLUE),
    queryFn: () => eventsApi.getReviewSummary(CLUE),
    staleTime: 60_000,
    retry: false,
  })
  const save = useMutation({
    mutationFn: (verdict: ReviewVerdict) => eventsApi.putEventReview(eventId, verdict),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.eventPose(eventId), fresh)
      qc.invalidateQueries({ queryKey: queryKeys.reviewSummary(CLUE) })
    },
  })

  const mine = pose.my_verdict
  const myRow = mine ? pose.reviews.find((r) => r.verdict === mine && r.user === me) ?? null : null
  const others = pose.reviews.filter((r) => !(mine && r.user === me))
  const counts = summary.data?.by_state[pose.hand_at_train.state]

  return (
    <div className="space-y-3" data-testid="pose-review">
      <ClueAnswer pose={pose} />
      <div role="group" aria-label={t('review.groupLabel')} className="flex flex-wrap gap-2">
        {VERDICTS.map((v) => {
          const pressed = mine === v
          return (
            <button
              key={v}
              type="button"
              aria-pressed={pressed}
              disabled={save.isPending}
              onClick={() => { if (!pressed) save.mutate(v) }}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-wait',
                v === 'graffiti' && 'border-rose-600 text-rose-700 dark:border-rose-400 dark:text-rose-300',
                v === 'no_hand' && 'border-border text-foreground',
                v === 'hand_not_graffiti' && 'border-emerald-600 text-emerald-700 dark:border-emerald-400 dark:text-emerald-300',
                pressed && (v === 'graffiti'
                  ? 'bg-rose-500/15' : v === 'hand_not_graffiti' ? 'bg-emerald-500/15' : 'bg-accent'),
              )}
            >
              {t(`review.verdict.${v}`)}
            </button>
          )
        })}
      </div>
      {save.isError && <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">{t('review.saveFailed')}</p>}
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {mine
          ? t('review.markedBy', {
              verdict: t(`review.verdict.${mine}`),
              user: myRow?.user ?? t('review.you'),
              time: myRow ? formatDateTime(myRow.updated_at) : '',
            })
          : t('review.notReviewed')}
      </p>
      {others.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground" aria-label={t('review.others')}>
          {others.map((r) => (
            <li key={r.user}>{t('review.otherMark', { user: r.user, verdict: t(`review.verdict.${r.verdict}`), time: formatDateTime(r.updated_at) })}</li>
          ))}
        </ul>
      )}
      {counts && (
        <p className="text-xs text-muted-foreground" data-testid="pose-review-counts">
          {t('review.counts', { state: t(`review.state.${pose.hand_at_train.state}`), ...counts })}
        </p>
      )}
    </div>
  )
}

function PoseReviewInner({ event }: { event: Event }) {
  const { t } = useTranslation('events')
  const { data: pose, isError } = useQuery({
    queryKey: queryKeys.eventPose(event.id),
    queryFn: () => eventsApi.getEventPose(event.id),
    staleTime: POSE_STALE_MS,
    retry: false,
  })
  return (
    <div className="p-5 pb-4 border-b border-border/60 flex-shrink-0">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">{t('review.title')}</p>
      {pose ? <PoseReviewMarks eventId={event.id} pose={pose} />
        : isError ? <p role="status" className="text-sm text-muted-foreground">{t('detail.poseUnavailable')}</p>
        : <p role="status" className="text-sm text-muted-foreground">{t('review.loading')}</p>}
    </div>
  )
}

/** Below the media, superusers only, on an event that has a stored pose (no request otherwise). */
export function PoseReviewPanel({ event }: { event: Event }) {
  // `=== true`: a store that is not loaded, or a test double, never counts as a superuser. The gate (a query) is
  // only mounted for a superuser, so everyone else needs no QueryClient and makes no request.
  const isSuperuser = useAuthStore((s) => s?.user?.is_superuser === true)
  if (!isSuperuser) return null
  return <PoseGate event={event}><PoseReviewInner event={event} /></PoseGate>
}

export { PoseReviewMarks }
